import type { CSSProperties } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SYMBOL_PATH =
  'M29.3081 105.276C28.0439 105.621 26.2049 105.966 23.7913 106.31C21.4926 106.655 18.964 106.828 16.2056 106.828C10.459 106.828 6.32137 105.908 3.79284 104.069C1.26432 102.115 3.07737e-05 98.4374 3.07737e-05 93.0356C3.07737e-05 93.0356 -3.84671e-05 86.8903 3.07737e-05 81.7325C0.000100015 76.575 2.49843 68.7039 7.52393 64.6633C12.5495 60.6227 21.3453 58.8357 21.3453 58.8357C21.3453 58.8357 7.58599 53.125 3.79284 46.1859C-0.00030106 39.2466 3.07737e-05 28.6764 3.07737e-05 28.6764L1.60727e-05 1.55161C1.26428 1.2068 3.04577 0.861999 5.34442 0.517195C7.75801 0.172398 10.3441 0 13.1025 0C18.849 0 22.9867 0.976935 25.5153 2.93081C28.0438 4.76974 29.3081 8.39015 29.3081 13.792V50.7975L58.961 20.4549C64.9375 20.4549 69.7073 21.7767 73.2702 24.4201C76.9481 27.0637 78.7871 30.3968 78.7871 34.4195C78.7871 36.1435 78.4997 37.7525 77.925 39.2466C77.3503 40.6259 76.4309 42.0625 75.1666 43.5566C74.0173 45.0508 71.7868 47.6836 69.8328 49.4074C67.994 51.0166 65.5122 54.8508 58.0342 56.9609C50.5563 59.0708 37.5077 58.8357 37.5077 58.8357L81.5455 90.2772C80.9708 95.679 79.3616 99.8167 76.7182 102.69C74.1897 105.563 70.4543 107 65.5122 107C61.8344 107 58.5586 106.138 55.6854 104.414C52.8121 102.575 49.7088 99.5293 46.3757 95.2768L29.3081 66.9169V105.276Z'

// Wedge intersections rebuilt from the frozen splash specification. Each
// wedge rotates around the coral dot pivot; together they form the complete
// symbol at rest.
const WEDGES = [
  '29.06,57.72 29.29,50.79 141.67,-227.37 210.65,-298.69 241.02,-281.50 269.78,-261.74 296.71,-239.54 321.60,-215.08 344.26,-188.55 364.52,-160.14 382.23,-130.07 397.26,-98.58 409.48,-65.89 418.80,-32.26 425.16,2.05 428.51,36.78 428.81,71.68 426.07,106.46 420.31,140.88 324.45,146.56 37.56,58.85',
  '29.06,57.72 21.29,58.85 -278.71,58.85 -369.42,22.85 -364.87,-11.74 -357.31,-45.81 -346.82,-79.09 -333.47,-111.33 -317.35,-142.28 -298.61,-171.71 -277.36,-199.40 -253.79,-225.13 -228.06,-248.70 -200.38,-269.94 -170.94,-288.69 -139.99,-304.81 -107.75,-318.16 -74.47,-328.65 -40.40,-336.21 -5.81,-340.76 29.06,-342.28 63.92,-340.76 98.51,-336.21 132.58,-328.65 165.86,-318.16 141.67,-227.37 29.29,50.79',
  '29.06,57.72 29.29,67.07 106.94,356.85 98.51,451.64 63.92,456.19 29.06,457.72 -5.81,456.19 -40.40,451.64 -74.47,444.09 -107.75,433.59 -139.99,420.24 -170.94,404.13 -200.38,385.38 -228.06,364.13 -253.79,340.56 -277.36,314.83 -298.61,287.15 -317.35,257.72 -333.47,226.76 -346.82,194.52 -357.31,161.24 -364.87,127.18 -369.42,92.58 -278.71,58.85 21.29,58.85',
  '29.06,57.72 37.56,58.85 324.45,146.56 399.93,207.56 385.46,239.31 368.27,269.68 348.51,298.44 326.31,325.37 301.85,350.26 275.32,372.92 246.91,393.18 216.84,410.90 185.35,425.92 152.66,438.14 106.94,356.85 29.29,67.07',
] as const

export const SPLASH_DURATION_MS = 1800
export const SPLASH_FADE_MS = 280

export interface SplashScreenProps {
  onDone?: () => void
  durationMs?: number
}

/**
 * Brand splash: full teal, coral dot first, four paper petals counter-clockwise,
 * 1.8s plus a 280ms fade. Reduced motion jumps to the final mark.
 */
export function SplashScreen({
  onDone,
  durationMs = SPLASH_DURATION_MS,
}: SplashScreenProps) {
  const { t } = useTranslation()
  const rawId = useId()
  const prefix = rawId.replaceAll(':', '')
  const onDoneRef = useRef(onDone)
  const [fading, setFading] = useState(false)
  const fadeDelayMs = Math.max(durationMs - SPLASH_FADE_MS, 0)

  useEffect(() => {
    onDoneRef.current = onDone
  }, [onDone])

  useEffect(() => {
    const reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reducedMotion) {
      onDoneRef.current?.()
      return
    }

    // Opacity zero does not remove a node from hit testing. If the page is
    // backgrounded, the unmount timer can be delayed and the transparent
    // splash would swallow every tap, so stop hit testing when the fade
    // starts (280ms before the component unmounts).
    const fadeTimer = setTimeout(() => setFading(true), fadeDelayMs)
    const doneTimer = setTimeout(() => onDoneRef.current?.(), durationMs)
    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(doneTimer)
    }
  }, [durationMs, fadeDelayMs])

  return (
    <div
      className={`kuniri-splash${fading ? ' kuniri-splash--fading' : ''}`}
      role="status"
      aria-label={t('a11y.splashLabel')}
      data-testid="splash"
      style={{ '--splash-fade-delay': `${fadeDelayMs}ms` } as CSSProperties}
    >
      <svg
        className="kuniri-splash__symbol"
        viewBox="0 0 82 107"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {WEDGES.map((points, index) => (
            <clipPath
              key={`wedge-${index}`}
              id={`${prefix}-wedge-${index}`}
              clipPathUnits="userSpaceOnUse"
            >
              <polygon points={points} />
            </clipPath>
          ))}
        </defs>
        {WEDGES.map((_, index) => (
          <g
            key={`petal-${index}`}
            clipPath={`url(#${prefix}-wedge-${index})`}
            className={`kuniri-splash__petal kuniri-splash__petal--${index + 1}`}
          >
            <path d={SYMBOL_PATH} fill="var(--paper)" />
          </g>
        ))}
        <g className="kuniri-splash__dot">
          <circle cx="29.0555" cy="57.7162" r="13.35" fill="var(--coral)" />
        </g>
      </svg>
    </div>
  )
}
